import { ClaimApi, CoreFetch } from '../../api';
import { FilterMethod, ICustomFilter } from '../data-table/components/filters/filters.models';

/** Sentinel column filter — never sent to GET /messages; resolved to filterClaimId. */
export const PATIENT_NAME_FILTER_ID = 'filterPatientName';
/** Legacy messages patient filter — do not send; Patient search uses GET /claims → filterClaimId. */
export const PATIENT_ID_FILTER_ID = 'filterPatientId';

const CLAIMS_PAGE_SIZE = 100;
const CLAIMS_MAX_PAGES = 50;

const optionId = (raw: unknown): string => {
    if (raw == null || raw === '') {
        return '';
    }
    if (typeof raw === 'object' && raw !== null && 'value' in raw) {
        return String((raw as { value?: unknown }).value ?? '').trim();
    }
    return String(raw).trim();
};

/** List endpoints sometimes return a bare array, sometimes `{ claims|data|items }`. */
export function asClaimRows(result: unknown): Array<{ id?: string; claimId?: string }> {
    if (Array.isArray(result)) {
        return result;
    }
    if (result && typeof result === 'object') {
        const obj = result as Record<string, unknown>;
        for (const key of ['claims', 'data', 'items', 'results', 'rows', 'content', 'records']) {
            if (Array.isArray(obj[key])) {
                return obj[key] as Array<{ id?: string; claimId?: string }>;
            }
        }
        if (obj.data && typeof obj.data === 'object') {
            return asClaimRows(obj.data);
        }
    }
    return [];
}

export function claimRowId(row: unknown): string {
    if (row == null) {
        return '';
    }
    if (typeof row === 'string' || typeof row === 'number') {
        return String(row).trim();
    }
    if (typeof row !== 'object') {
        return '';
    }
    const rec = row as Record<string, unknown>;
    const direct = rec.id ?? rec.claimId;
    if (typeof direct === 'string' || typeof direct === 'number') {
        return String(direct).trim();
    }
    if (rec.claim && typeof rec.claim === 'object') {
        return claimRowId(rec.claim);
    }
    return '';
}

async function readClaimListJson(response: Response): Promise<unknown> {
    // Clone first — auth/abort/contract middleware already tees the fetch body.
    // json() on the same stream empties Chrome's Response tab and throws if locked.
    const text = await response.clone().text();
    if (!text) {
        return [];
    }
    return JSON.parse(text);
}

export function parsePatientIdsFromFilters(filters?: ICustomFilter[]): string[] {
    const ids = new Set<string>();
    for (const filter of filters || []) {
        if (filter.filterId !== PATIENT_NAME_FILTER_ID || filter.value == null || filter.value === '') {
            continue;
        }
        const values = Array.isArray(filter.value) ? filter.value : [filter.value];
        for (const value of values) {
            const id = optionId(value);
            if (id) {
                ids.add(id);
            }
        }
    }
    return [...ids];
}

export function buildClaimIdFilters(claimIds: string[]): ICustomFilter[] {
    return claimIds.map(id => ({
        filterId: 'filterClaimId',
        filterMethod: FilterMethod.EQ,
        filterType: 'String',
        value: id,
        label: 'Claim',
    }));
}

/** Resolve selected patients to GET /messages extras, or `'empty'` when no claims match. */
export async function resolvePatientClaimIdExtra(options: {
    listFilters?: ICustomFilter[];
    resolvePatientClaimIds?: (patientIds: string[]) => Promise<string[]>;
    isSupport?: boolean;
}): Promise<ICustomFilter[] | 'empty'> {
    const patientIds = parsePatientIdsFromFilters(options.listFilters);
    if (!patientIds.length) {
        return [];
    }
    const claimIds = options.resolvePatientClaimIds
        ? await options.resolvePatientClaimIds(patientIds)
        : await collectClaimIdsFromPatientClaims({
              patientIds,
              isSupport: options.isSupport,
          });
    if (claimIds.length === 0) {
        return 'empty';
    }
    return buildClaimIdFilters(claimIds);
}

let patientClaimCacheKey: string | null = null;
let patientClaimCacheIds: string[] | null = null;
let patientClaimInflight: Promise<string[]> | null = null;

export function clearPatientClaimIdCache(): void {
    patientClaimCacheKey = null;
    patientClaimCacheIds = null;
    patientClaimInflight = null;
}

const cacheKey = (patientIds: string[]): string => [...patientIds].sort().join('\0');

export async function listClaimsForPatientIds(options: {
    isSupport: boolean;
    patientIds: string[];
    limit: number;
    offset: number;
}): Promise<Array<{ id?: string; claimId?: string }>> {
    const patientIds = options.patientIds.filter(Boolean);
    if (!patientIds.length) {
        return [];
    }
    const reqFilters: Record<string, string[]> = {
        filterPatientId: patientIds.map(id => `EQ:${id}`),
    };
    if (options.isSupport) {
        reqFilters.filterActive = ['EQ:1'];
    }
    const request = {
        ...reqFilters,
        limit: options.limit,
        offset: options.offset,
    };
    const api = CoreFetch<ClaimApi>(ClaimApi);
    try {
        const raw = options.isSupport
            ? await api.listSupportClaimsRaw(request)
            : await api.listProviderClaimsRaw(request);
        // Do not call value() — generated mapper does jsonValue.map(...) and throws on
        // `{ claims: [...] }`, which is a 200 with Preview data and no GET /messages.
        return asClaimRows(await readClaimListJson(raw.raw));
    } catch (err) {
        if (err instanceof Response) {
            throw new Error(`GET /claims failed (${err.status})`);
        }
        throw err;
    }
}

/**
 * Page GET /claims by ENTER patient id (same filter as the claims-list Patient column).
 * Prefer this over HL7-by-externalId: messages attach to claims, and the picker already
 * returns the patient UUID claims are indexed on.
 */
export async function collectClaimIdsFromPatientClaims(options: {
    patientIds: string[];
    isSupport?: boolean;
    listPage?: (offset: number, limit: number) => Promise<Array<{ id?: string; claimId?: string }>>;
    pageSize?: number;
    maxPages?: number;
}): Promise<string[]> {
    const patientIds = [...new Set(options.patientIds.map(id => id.trim()).filter(Boolean))];
    if (!patientIds.length) {
        return [];
    }

    const key = cacheKey(patientIds);
    if (patientClaimCacheKey === key && patientClaimCacheIds) {
        return patientClaimCacheIds;
    }
    if (patientClaimCacheKey === key && patientClaimInflight) {
        return patientClaimInflight;
    }

    const pageSize = options.pageSize ?? CLAIMS_PAGE_SIZE;
    const maxPages = options.maxPages ?? CLAIMS_MAX_PAGES;
    const listPage =
        options.listPage ??
        ((offset, limit) =>
            listClaimsForPatientIds({
                isSupport: !!options.isSupport,
                patientIds,
                offset,
                limit,
            }));

    patientClaimCacheKey = key;
    patientClaimCacheIds = null;
    const inflight = (async () => {
        const ids = new Set<string>();
        for (let page = 0; page < maxPages; page++) {
            const rows = asClaimRows(await listPage(page * pageSize, pageSize));
            for (const row of rows) {
                const id = claimRowId(row);
                if (id) {
                    ids.add(id);
                }
            }
            if (rows.length < pageSize) {
                break;
            }
        }
        return [...ids];
    })()
        .then(ids => {
            if (patientClaimCacheKey === key && patientClaimInflight === inflight) {
                patientClaimCacheIds = ids;
                patientClaimInflight = null;
            }
            return ids;
        })
        .catch(err => {
            if (patientClaimCacheKey === key && patientClaimInflight === inflight) {
                patientClaimInflight = null;
                patientClaimCacheKey = null;
                patientClaimCacheIds = null;
            }
            throw err;
        });
    patientClaimInflight = inflight;
    return inflight;
}
